import { Link } from 'react-router-dom';
import { ProofCounter } from '../ui/ProofCounter';


function ZodiacRing() {
  const size = 300;
  const cx = size / 2;
  const cy = size / 2;
  const r = size * 0.46;

  const signs = [
    { symbol: '♈', color: '#E85D4A' },
    { symbol: '♉', color: '#8BC34A' },
    { symbol: '♊', color: '#FFD54F' },
    { symbol: '♋', color: '#90CAF9' },
    { symbol: '♌', color: '#FF8A65' },
    { symbol: '♍', color: '#A5D6A7' },
    { symbol: '♎', color: '#CE93D8' },
    { symbol: '♏', color: '#EF9A9A' },
    { symbol: '♐', color: '#FFCC02' },
    { symbol: '♑', color: '#B0BEC5' },
    { symbol: '♒', color: '#29B6F6' },
    { symbol: '♓', color: '#80DEEA' },
  ];

  const planets = [
    { symbol: '☀', degree: 95,  color: '#FFD700' },
    { symbol: '☽', degree: 332, color: '#E0E0E0' },
    { symbol: '☿', degree: 78,  color: '#B0BEC5' },
    { symbol: '♀', degree: 115, color: '#F48FB1' },
    { symbol: '♂', degree: 210, color: '#EF5350' },
    { symbol: '♃', degree: 155, color: '#FF8F00' },
    { symbol: '♄', degree: 280, color: '#78909C' },
    { symbol: '♅', degree: 15,  color: '#80DEEA' },
    { symbol: '♆', degree: 245, color: '#7986CB' },
    { symbol: '♇', degree: 268, color: '#CE93D8' },
  ];

  const degToXY = (deg: number, radius: number) => {
    const rad = ((deg - 90) * Math.PI) / 180;
    return { x: cx + radius * Math.cos(rad), y: cy + radius * Math.sin(rad) };
  };

  const aspects: Array<[number, number, string]> = [
    [0, 1, 'rgba(255,215,0,0.18)'],
    [0, 2, 'rgba(100,181,246,0.14)'],
    [1, 5, 'rgba(255,143,0,0.14)'],
    [3, 4, 'rgba(239,83,80,0.12)'],
  ];

  return (
    <svg
      width={size}
      height={size}
      viewBox={`0 0 ${size} ${size}`}
      aria-hidden="true"
      style={{ display: 'block', filter: 'drop-shadow(0 0 24px rgba(201,168,76,0.25))' }}
    >
      <defs>
        <radialGradient id="nw-bg" cx="50%" cy="50%">
          <stop offset="0%" stopColor="#1a2040" />
          <stop offset="100%" stopColor="#0a0f20" />
        </radialGradient>
        <linearGradient id="nw-gold" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#C9A84C" />
          <stop offset="50%" stopColor="#E8C86A" />
          <stop offset="100%" stopColor="#C9A84C" />
        </linearGradient>
        <filter id="nw-glow">
          <feGaussianBlur stdDeviation="2" result="blur" />
          <feMerge><feMergeNode in="blur" /><feMergeNode in="SourceGraphic" /></feMerge>
        </filter>
        <style>{`
          @keyframes nw-rotate { to { transform: rotate(360deg); transform-origin: ${cx}px ${cy}px; } }
          @keyframes nw-pulse { 0%,100%{opacity:.75} 50%{opacity:1} }
          @keyframes nw-twinkle { 0%,100%{opacity:.2} 50%{opacity:.8} }
          .nw-outer { animation: nw-rotate 90s linear infinite; }
          .nw-planet { animation: nw-pulse 3s ease-in-out infinite; }
          @media (prefers-reduced-motion:reduce) { .nw-outer,.nw-planet { animation:none; } }
        `}</style>
      </defs>

      {/* Background */}
      <circle cx={cx} cy={cy} r={r * 1.06} fill="url(#nw-bg)" />

      {/* Starfield */}
      {Array.from({ length: 32 }, (_, i) => {
        const angle = (i * 137.5 * Math.PI) / 180;
        const dist = ((20 + (i * 17) % 55) / 100) * r;
        return (
          <circle key={i}
            cx={cx + dist * Math.cos(angle)} cy={cy + dist * Math.sin(angle)}
            r={i % 5 === 0 ? 1.2 : 0.7} fill="white"
            opacity={0.15 + (i % 4) * 0.08}
            style={{ animation: `nw-twinkle ${2 + (i % 3)}s ease-in-out ${i * 0.25}s infinite` }}
          />
        );
      })}

      {/* Outer zodiac ring, rotates */}
      <g className="nw-outer">
        {signs.map((sign, i) => {
          const sA = i * 30 - 90, eA = sA + 30;
          const sR = (sA * Math.PI) / 180, eR = (eA * Math.PI) / 180;
          const oR = r, iR = r * 0.78;
          const x1 = cx + oR * Math.cos(sR), y1 = cy + oR * Math.sin(sR);
          const x2 = cx + oR * Math.cos(eR), y2 = cy + oR * Math.sin(eR);
          const x3 = cx + iR * Math.cos(eR), y3 = cy + iR * Math.sin(eR);
          const x4 = cx + iR * Math.cos(sR), y4 = cy + iR * Math.sin(sR);
          const mR = (sA + 15) * Math.PI / 180;
          const sX = cx + r * 0.88 * Math.cos(mR), sY = cy + r * 0.88 * Math.sin(mR);
          return (
            <g key={i}>
              <path
                d={`M ${x1} ${y1} A ${oR} ${oR} 0 0 1 ${x2} ${y2} L ${x3} ${y3} A ${iR} ${iR} 0 0 0 ${x4} ${y4} Z`}
                fill={`${sign.color}18`} stroke="rgba(201,168,76,0.25)" strokeWidth="0.5"
              />
              <text x={sX} y={sY} textAnchor="middle" dominantBaseline="middle"
                fontSize={size * 0.042} fill={sign.color} opacity={0.9}>
                {sign.symbol}
              </text>
            </g>
          );
        })}
      </g>

      {/* Concentric circles */}
      {[0.77, 0.60, 0.42].map((ratio, i) => (
        <circle key={i} cx={cx} cy={cy} r={r * ratio}
          fill="none" stroke="rgba(201,168,76,0.18)" strokeWidth="0.5" />
      ))}

      {/* House division lines */}
      {Array.from({ length: 12 }, (_, i) => {
        const a = (i * 30 - 90) * Math.PI / 180;
        return (
          <line key={i}
            x1={cx + r * 0.42 * Math.cos(a)} y1={cy + r * 0.42 * Math.sin(a)}
            x2={cx + r * 0.77 * Math.cos(a)} y2={cy + r * 0.77 * Math.sin(a)}
            stroke="rgba(201,168,76,0.22)" strokeWidth="0.5"
          />
        );
      })}

      {/* Aspect lines */}
      {aspects.map(([a, b, color], i) => {
        const pA = degToXY(planets[a].degree, r * 0.50);
        const pB = degToXY(planets[b].degree, r * 0.50);
        return <line key={i} x1={pA.x} y1={pA.y} x2={pB.x} y2={pB.y}
          stroke={color} strokeWidth="0.8" />;
      })}

      {/* Planets */}
      {planets.map((planet, i) => {
        const pos = degToXY(planet.degree, r * 0.62);
        return (
          <g key={i} className="nw-planet" style={{ animationDelay: `${i * 0.35}s` }}>
            <circle cx={pos.x} cy={pos.y} r={size * 0.022}
              fill={`${planet.color}22`} filter="url(#nw-glow)" />
            <text x={pos.x} y={pos.y} textAnchor="middle" dominantBaseline="middle"
              fontSize={size * 0.036} fill={planet.color} filter="url(#nw-glow)" fontWeight="bold">
              {planet.symbol}
            </text>
          </g>
        );
      })}

      {/* Center ✦ */}
      <text x={cx} y={cy} textAnchor="middle" dominantBaseline="middle"
        fontSize={size * 0.055} fill="url(#nw-gold)" filter="url(#nw-glow)"
        className="nw-planet">✦</text>

      {/* Gold border */}
      <circle cx={cx} cy={cy} r={r * 1.04}
        fill="none" stroke="url(#nw-gold)" strokeWidth="1.5" opacity="0.55" />
    </svg>
  );
}

export function Hero() {
  return (
    <section
      style={{
        padding: 'clamp(5rem, 12vw, 9rem) 2rem clamp(4rem, 8vw, 7rem)',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Subtle starfield background */}
      <svg
        aria-hidden="true"
        style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', opacity: 0.35, pointerEvents: 'none' }}
        xmlns="http://www.w3.org/2000/svg"
      >
        {Array.from({ length: 48 }).map((_, i) => (
          <circle
            key={i}
            cx={`${(i * 73 + 11) % 100}%`}
            cy={`${(i * 43 + 7) % 100}%`}
            r={i % 5 === 0 ? 1.2 : 0.6}
            fill="#9CBCD0"
            opacity={0.3 + (i % 3) * 0.15}
          />
        ))}
      </svg>

      <div style={{
        maxWidth: '1100px',
        margin: '0 auto',
        display: 'grid',
        gridTemplateColumns: '1fr auto',
        gap: '4rem',
        alignItems: 'center',
        position: 'relative',
      }}>
        {/* Left: copy */}
        <div style={{ maxWidth: '560px' }}>
          <ProofCounter />
          <p className="eyebrow" style={{ marginBottom: '1.25rem', color: '#D4AF37' }}>
            Astrologia personalizada para você
          </p>

          <h1 style={{ margin: '0 0 1.5rem', lineHeight: 1.1 }}>
            <em style={{ color: '#F4E4C1', fontStyle: 'italic' }}>Segredo Astral</em>
          </h1>

          <p style={{
            fontFamily: 'var(--font-display)',
            fontSize: 'var(--text-18)',
            fontWeight: 300,
            color: 'rgba(200,220,235,0.8)',
            lineHeight: 1.75,
            margin: '0 0 2.5rem',
            maxWidth: '460px',
          }}>
            Mapa astral completo, com mais de 20 sistemas de leitura e uma interpretação
            feita para você, não genérica para seu signo.
          </p>

          {/* CTAs */}
          <div style={{ display: 'flex', gap: '1rem', alignItems: 'center', flexWrap: 'wrap' }}>
            <Link to="/register" className="btn btn-primary btn-lg" style={{
              background: 'linear-gradient(135deg, #D4AF37 0%, #F4E4C1 100%)',
              borderColor: '#D4AF37',
              color: '#0A0416',
              fontWeight: 700,
            }}>
              Ver meu mapa natal grátis
            </Link>
            <Link to="/#ritual" className="btn btn-ghost btn-lg" style={{
              color: 'rgba(240,246,252,0.8)',
              borderColor: 'rgba(212,175,55,0.3)',
            }}>
              Como funciona
            </Link>
          </div>

        </div>

        {/* Right: zodiac art */}
        <div
          className="hero-illustration"
          style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}
        >
          <ZodiacRing />
        </div>
      </div>

      <style>{`
        @keyframes zodiac-spin {
          to { transform: rotate(360deg); }
        }
        @media (max-width: 767px) {
          .hero-illustration { display: none !important; }
        }
      `}</style>

    </section>
  );
}
